import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import GroupIcon from '@mui/icons-material/Group';
import CalibrationGallery from '../components/analytics/CalibrationGallery';
import TeamVotingPatterns from '../components/analytics/TeamVotingPatterns';
import IndividualTendencies from '../components/analytics/IndividualTendencies';
import PointDriftChart from '../components/analytics/PointDriftChart';
import SprintComparison from '../components/analytics/SprintComparison';
import AccuracySummaryCard from '../components/analytics/AccuracySummaryCard';
import AccuracyTrendChart from '../components/analytics/AccuracyTrendChart';
import AccuracyByEstimate from '../components/analytics/AccuracyByEstimate';
import { useWorkspace } from '../context/WorkspaceContext';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index) {
  return {
    id: `analytics-tab-${index}`,
    'aria-controls': `analytics-tabpanel-${index}`,
  };
}

const AnalyticsDashboardPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tabValue, setTabValue] = useState(0);
  const {
    organizations,
    teams,
    userTeams,
    currentOrganization,
    currentTeam,
    workspaceMode,
    refreshTeams,
    isOrgAdmin,
  } = useWorkspace();

  // Analytics filter state
  const [analyticsScope, setAnalyticsScope] = useState('personal');
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [selectedTeamId, setSelectedTeamId] = useState('');

  // Initialize scope based on current workspace
  useEffect(() => {
    if (workspaceMode === 'team' && currentTeam) {
      setAnalyticsScope('team');
      setSelectedOrgId(currentOrganization?.id || '');
      setSelectedTeamId(currentTeam.id);
    } else if (workspaceMode === 'organization' && currentOrganization) {
      // Only org admins can view org-wide analytics
      const userIsOrgAdmin = ['owner', 'admin'].includes(currentOrganization.role);
      if (userIsOrgAdmin) {
        setAnalyticsScope('organization');
        setSelectedOrgId(currentOrganization.id);
        setSelectedTeamId('');
      } else {
        // Non-admins default to team scope
        setAnalyticsScope('team');
        setSelectedOrgId(currentOrganization.id);
        setSelectedTeamId('');
      }
    } else {
      setAnalyticsScope('personal');
      setSelectedOrgId('');
      setSelectedTeamId('');
    }
  }, [workspaceMode, currentOrganization, currentTeam]);

  // Handle URL parameter for initial estimate filter
  useEffect(() => {
    const estimateParam = searchParams.get('estimate');
    if (estimateParam) {
      // Navigate to Calibration Gallery tab if estimate is specified
      setTabValue(0);
    }
  }, [searchParams]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    // Clear URL params when changing tabs
    if (searchParams.has('estimate')) {
      setSearchParams({});
    }
  };

  const handleScopeChange = (event) => {
    const newScope = event.target.value;
    setAnalyticsScope(newScope);
    if (newScope === 'personal') {
      setSelectedOrgId('');
      setSelectedTeamId('');
    }
  };

  const handleOrgChange = async (event) => {
    const orgId = event.target.value;
    setSelectedOrgId(orgId);
    setSelectedTeamId('');
    if (orgId) {
      await refreshTeams(orgId);
      // Non-admins should default to team scope, not org scope
      // isOrgAdmin() checks currentOrganization, so we need to find the org
      const org = organizations.find(o => o.id === orgId);
      const userIsOrgAdmin = org && ['owner', 'admin'].includes(org.role);
      setAnalyticsScope(userIsOrgAdmin ? 'organization' : 'team');
    }
  };

  const handleTeamChange = (event) => {
    const teamId = event.target.value;
    setSelectedTeamId(teamId);
    if (teamId) {
      setAnalyticsScope('team');
    } else {
      // When clearing team, only admins can view org-wide scope
      const org = organizations.find(o => o.id === selectedOrgId);
      const userIsOrgAdmin = org && ['owner', 'admin'].includes(org.role);
      setAnalyticsScope(userIsOrgAdmin ? 'organization' : 'team');
    }
  };

  // Build filter props for child components
  const filterProps = {
    scope: analyticsScope,
    organizationId: analyticsScope === 'organization' || analyticsScope === 'team' ? selectedOrgId : undefined,
    teamId: analyticsScope === 'team' ? selectedTeamId : undefined,
  };

  const getScopeLabel = () => {
    if (analyticsScope === 'team' && selectedTeamId) {
      const team = teams.find(t => t.id === selectedTeamId);
      return team?.name || 'Team';
    }
    if (analyticsScope === 'organization' && selectedOrgId) {
      const org = organizations.find(o => o.id === selectedOrgId);
      return org?.name || 'Organization';
    }
    return 'Personal';
  };

  const getScopeIcon = () => {
    if (analyticsScope === 'team') return <GroupIcon fontSize="small" />;
    if (analyticsScope === 'organization') return <BusinessIcon fontSize="small" />;
    return <PersonIcon fontSize="small" />;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link
          component={RouterLink}
          to="/dashboard"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          color="inherit"
          underline="hover"
        >
          <HomeIcon fontSize="small" />
          Dashboard
        </Link>
        <Typography
          color="text.primary"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          <AnalyticsIcon fontSize="small" />
          Analytics
        </Typography>
      </Breadcrumbs>

      {/* Header */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Estimation Analytics
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Analyze estimation patterns, calibrate team consistency, and track how estimates evolve over time.
            </Typography>
          </Box>

          {/* Workspace Filter */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <Chip
              icon={getScopeIcon()}
              label={`Viewing: ${getScopeLabel()}`}
              variant="outlined"
              color="primary"
            />

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Scope</InputLabel>
              <Select
                value={analyticsScope}
                label="Scope"
                onChange={handleScopeChange}
              >
                <MenuItem value="personal">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    Personal
                  </Box>
                </MenuItem>
                {/* Organization-wide scope only for org admins */}
                {organizations.length > 0 && isOrgAdmin() && (
                  <MenuItem value="organization">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon fontSize="small" />
                      Organization
                    </Box>
                  </MenuItem>
                )}
                {/* Team scope available if user has teams */}
                {selectedOrgId && (isOrgAdmin() ? teams.length > 0 : userTeams.length > 0) && (
                  <MenuItem value="team">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <GroupIcon fontSize="small" />
                      Team
                    </Box>
                  </MenuItem>
                )}
              </Select>
            </FormControl>

            {(analyticsScope === 'organization' || analyticsScope === 'team') && organizations.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Organization</InputLabel>
                <Select
                  value={selectedOrgId}
                  label="Organization"
                  onChange={handleOrgChange}
                >
                  {organizations.map((org) => (
                    <MenuItem key={org.id} value={org.id}>
                      {org.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {analyticsScope === 'team' && selectedOrgId && (isOrgAdmin() ? teams.length > 0 : userTeams.length > 0) && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Team</InputLabel>
                <Select
                  value={selectedTeamId}
                  label="Team"
                  onChange={handleTeamChange}
                >
                  {/* Org admins see all teams, others see only their teams */}
                  {(isOrgAdmin() ? teams : userTeams).map((team) => (
                    <MenuItem key={team.id} value={team.id}>
                      {team.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={2}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="analytics tabs"
            variant="fullWidth"
          >
            <Tab label="Calibration Gallery" {...a11yProps(0)} />
            <Tab label="Team Patterns" {...a11yProps(1)} />
            <Tab label="Trends" {...a11yProps(2)} />
            <Tab label="Accuracy" {...a11yProps(3)} />
            <Tab label="Sprint Comparison" {...a11yProps(4)} />
          </Tabs>
        </Box>

        <Box sx={{ p: 3 }}>
          {/* Calibration Gallery Tab */}
          <TabPanel value={tabValue} index={0}>
            <CalibrationGallery {...filterProps} />
          </TabPanel>

          {/* Team Patterns Tab */}
          <TabPanel value={tabValue} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <TeamVotingPatterns {...filterProps} />
              <IndividualTendencies {...filterProps} />
            </Box>
          </TabPanel>

          {/* Trends Tab */}
          <TabPanel value={tabValue} index={2}>
            <PointDriftChart {...filterProps} />
          </TabPanel>

          {/* Accuracy Tab */}
          <TabPanel value={tabValue} index={3}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <AccuracySummaryCard {...filterProps} />
              <AccuracyTrendChart {...filterProps} />
              <AccuracyByEstimate {...filterProps} />
            </Box>
          </TabPanel>

          {/* Sprint Comparison Tab */}
          <TabPanel value={tabValue} index={4}>
            <SprintComparison {...filterProps} />
          </TabPanel>
        </Box>
      </Paper>
    </Container>
  );
};

export default AnalyticsDashboardPage;
